import type { BusinessVariant } from '@/api/billing'
import type { NoteSharingAudience } from '@/types'

export const PLATFORM_ADMIN_PORTAL = 'admin_portal' as const

export enum EventName {
  ManageBillingClicked = 'manage_billing_clicked',

  // Member management
  InviteSent = 'team_invite_sent',
  BulkInviteSent = 'team_bulk_invite_sent',
  InviteRevoked = 'admin_portal_invite_revoked',
  MemberRemoved = 'admin_portal_member_removed',
  MemberNotetakerDataErasureStarted = 'admin_portal_member_notetaker_data_erasure_started',
  RoleChanged = 'admin_portal_role_changed',
  JoinRequestApproved = 'team_join_request_approved',
  JoinRequestDismissed = 'team_join_request_denied',

  // Checkout & billing
  CheckoutStarted = 'checkout_started',
  // Shared keys with desktop: one cross-platform funnel, split by the platform property.
  BusinessPlanUpgradeClicked = 'business_plan_upgrade_clicked',
  BusinessPlanOptionSelected = 'business_plan_option_selected',
  TalkToSalesClicked = 'talk_to_sales_clicked',
  PlanSwitchCompleted = 'admin_portal_plan_switch_completed',
  BillingPortalOpened = 'admin_portal_billing_portal_opened',

  // Billing config (internal admin)
  BillingConfigCreated = 'admin_portal_billing_config_created',
  BillingConfigEdited = 'admin_portal_billing_config_edited',
  BillingConfigDeleted = 'admin_portal_billing_config_deleted',

  // User trial (internal admin)
  UpdatedUserTrial = 'admin_portal_updated_user_trial',

  // Stripe customer ID (internal admin)
  UpdatedStripeCustomerId = 'admin_portal_updated_stripe_customer_id',

  // Password reset (internal admin)
  RequestedUserPasswordReset = 'admin_portal_requested_user_password_reset',

  // Dictionary & snippets (internal admin)
  ExportedUserDictionary = 'admin_portal_exported_user_dictionary',
  ImportedUserDictionary = 'admin_portal_imported_user_dictionary',

  // Settings
  SettingChanged = 'admin_portal_setting_changed',
  MeetingNoteSharingChanged = 'meeting_note_sharing_changed',

  // Enterprise adoption
  SSOSetupCompleted = 'admin_portal_sso_setup_completed',
  SCIMSetupCompleted = 'admin_portal_scim_setup_completed',
  AutoInviteToggled = 'admin_portal_auto_invite_toggled',
  OrgNotetakerEnabledChanged = 'org_notetaker_enabled_changed',

  // Checkout funnel
  BillingInfoSubmitted = 'admin_portal_billing_info_submitted',

  // Onboarding
  OnboardingTeamInvited = 'admin_portal_onboarding_team_invited',

  // Usage
  ExportUsageClicked = 'admin_portal_export_usage_clicked',
  TeamInsightsExported = 'admin_portal_team_insights_exported',
  MemberDetailViewed = 'admin_portal_member_detail_viewed',
  AuditLogsExported = 'admin_portal_audit_logs_exported',

  // Engagement
  PageViewed = 'admin_portal_page_viewed',
  SeatBreakdownToggled = 'admin_portal_seat_breakdown_toggled',
  HomeCtaClicked = 'admin_portal_home_cta_clicked',

  // Unified cross-platform events (shared with desktop)
  TeamInviteModalOpened = 'team_invite_modal_opened',
  TeamDomainUserAddClicked = 'team_domain_user_add_clicked',
  TeamSelfInviteSent = 'team_self_invite_sent',
  TeamInviteRequestSent = 'team_invite_request_sent',
  TeamBillingModalOpened = 'team_billing_modal_opened',
  TeamInviteLinkCopied = 'team_invite_link_copied',
}

interface _EventPayload {
  [EventName.ManageBillingClicked]: undefined

  // Member management
  [EventName.InviteSent]: { user_id?: string; role: string; success: boolean; source: string; platform: string }
  [EventName.BulkInviteSent]: {
    count: number
    role_breakdown: Record<string, number>
    success: boolean
    source: string
    platform: string
  }
  [EventName.InviteRevoked]: { email: string }
  [EventName.MemberRemoved]: { user_id: string }
  [EventName.MemberNotetakerDataErasureStarted]: { user_id: string }
  [EventName.RoleChanged]: { user_id: string; new_role: string }
  [EventName.JoinRequestApproved]: { request_id: string; success: boolean; platform: string }
  [EventName.JoinRequestDismissed]: { request_id: string; success: boolean; platform: string }

  // Checkout & billing
  [EventName.CheckoutStarted]: {
    source: string
    platform: string
    isTeam: boolean
    plan_id?: string
    schedule?: string
    variant?: BusinessVariant
  }
  [EventName.BusinessPlanUpgradeClicked]: {
    option: BusinessVariant
    cycle: 'annual' | 'monthly'
    platform: string
  }
  [EventName.BusinessPlanOptionSelected]: {
    option: BusinessVariant
    cycle: 'annual' | 'monthly'
    platform: string
  }
  [EventName.TalkToSalesClicked]: {
    location: string
    platform: string
  }
  [EventName.PlanSwitchCompleted]: { plan: string; variant?: BusinessVariant }
  [EventName.BillingPortalOpened]: undefined

  // Billing config (internal admin)
  [EventName.BillingConfigCreated]: { enterprise_id: string; enterprise_name: string }
  [EventName.BillingConfigEdited]: { config_id: string; enterprise_name: string }
  [EventName.BillingConfigDeleted]: { config_id: string; enterprise_name: string }

  // User trial (internal admin)
  [EventName.UpdatedUserTrial]: { user_id: string; trial_ends_at: number }

  // Stripe customer ID (internal admin)
  [EventName.UpdatedStripeCustomerId]: { user_id: string }

  // Password reset (internal admin)
  [EventName.RequestedUserPasswordReset]: { user_id: string }

  // Dictionary & snippets (internal admin)
  [EventName.ExportedUserDictionary]: { user_id: string }
  [EventName.ImportedUserDictionary]: { user_id: string; rows_imported: number; rows_skipped: number }

  // Settings
  [EventName.SettingChanged]: { setting: string; value: string | boolean; success: boolean }
  [EventName.MeetingNoteSharingChanged]: { audience: NoteSharingAudience }

  // Enterprise adoption
  [EventName.SSOSetupCompleted]: undefined
  [EventName.SCIMSetupCompleted]: undefined
  [EventName.AutoInviteToggled]: { enabled: boolean }
  [EventName.OrgNotetakerEnabledChanged]: { enabled: boolean }

  // Checkout funnel
  [EventName.BillingInfoSubmitted]: { plan_type: string; schedule: string }

  // Onboarding
  [EventName.OnboardingTeamInvited]: { count: number }

  // Usage
  [EventName.ExportUsageClicked]: { member_count: number }
  [EventName.TeamInsightsExported]: { tab: 'overview' | 'team-members' }
  [EventName.MemberDetailViewed]: { user_id: string; viewed_from: 'table_row_click' | 'deep_link' }
  [EventName.AuditLogsExported]: {
    row_count: number
    event_type_count: number
    capped: boolean
    format: 'csv' | 'json'
  }

  // Engagement
  [EventName.PageViewed]: { page_path: string }
  [EventName.SeatBreakdownToggled]: { expanded: boolean }
  [EventName.HomeCtaClicked]: { target: 'team_members' | 'usage' | 'billing' | 'invite_teammates' }

  // Unified cross-platform events
  [EventName.TeamInviteModalOpened]: { source: string; platform: string }
  [EventName.TeamDomainUserAddClicked]: { email_domain: string; platform: string }
  [EventName.TeamSelfInviteSent]: { success: boolean; source: string; platform: string }
  [EventName.TeamInviteRequestSent]: { success: boolean; source: string; platform: string }
  [EventName.TeamBillingModalOpened]: { source: string; platform: string }
  [EventName.TeamInviteLinkCopied]: { source: string; platform: string; success: boolean }
}

export type EventPayload<T extends EventName> = _EventPayload[T]
